// Aliados (armerías referidoras): helpers compartidos entre el dashboard
// (página Aliados / estado de cuenta) y el portal público /armeros.
//
// Modelo: el aliado inscribe a su cliente en un curso desde su portal; el
// cliente le paga a la armería; BBTC le factura a la armería a fin de mes
// (precio pactado por curso) y le descuenta $5 por referido que ASISTIÓ.
// Desde la V3 de las instrucciones (2026-09-24): cada inasistencia y cada
// cancelación con menos de 24 h le cuesta a la armería una penalidad ($20),
// sin comisión.
// El portal se movió de /aliados a /armeros (2026-09-24); /aliados redirige.
export const PARTNER_PORTAL_PATH = '/armeros';

// Horas antes del curso hasta las que la armería (y el cliente desde la app)
// puede cambiar la fecha sin penalidad. El servidor usa la ventana de la
// categoría, rescheduleHoursBefore; esto es el default de cursos.
export const PARTNER_CHANGE_HOURS = 24;

export const DEFAULT_PARTNER_COMMISSION_CENTS = 500;
export const DEFAULT_PARTNER_PENALTY_CENTS = 2000;

// Curso de licencia nueva, por el nombre ("Licencia Nueva"), igual que el
// privado se detecta por "privado".
export const isNewLicenseCourseName = (name) => /nuev[ao]/i.test(String(name || '')) && !/renov/i.test(String(name || ''));

// Precio sugerido al crear una armería (V3): $65 renovación, $85 licencia nueva
export function suggestedPartnerPriceCents(courseName) {
  if (/renov/i.test(String(courseName || ''))) return 6500;
  if (isNewLicenseCourseName(courseName)) return 8500;
  return null;
}

// ¿El cliente paga renta de arma y municiones en B&B? Solo con licencia nueva,
// o en renovación si la licencia está vencida el día del curso.
// Devuelve 'expired', 'new' o null (no paga nada en B&B).
export function partnerRentalReason(r) {
  if (!r) return null;
  if (r.licenseExpiration && r.date && r.licenseExpiration < r.date) return 'expired';
  if (isNewLicenseCourseName(r.categoryName)) return 'new';
  return null;
}

export const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

export const toCents = (dollars) => Math.round((parseFloat(String(dollars).replace(/[^0-9.]/g, '')) || 0) * 100);

// 'YYYY-MM' del mes actual en hora local
export function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function monthLabel(key) {
  if (!key) return '';
  const [y, m] = key.split('-').map(Number);
  const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  return `${MESES[m - 1]} ${y}`;
}

// Últimos N meses como 'YYYY-MM' (el actual primero)
export function recentMonthKeys(n = 12) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i += 1) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

// Próximos N meses como 'YYYY-MM' (el siguiente primero). En el portal van
// junto a los recientes para ver las inscripciones del mes que viene.
export function upcomingMonthKeys(n = 3) {
  const out = [];
  const d = new Date();
  for (let i = 1; i <= n; i += 1) {
    const x = new Date(d.getFullYear(), d.getMonth() + i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

export const PARTNER_STATUS = {
  completed: { label: 'Asistió', color: 'success' },
  no_show: { label: 'No se presentó', color: 'error' },
  cancelled: { label: 'Cancelada', color: 'default' },
  confirmed: { label: 'Confirmada', color: 'info' },
  pending_payment: { label: 'Pendiente', color: 'warning' },
};

export function partnerStatus(status) {
  return PARTNER_STATUS[status] || { label: status || '—', color: 'default' };
}

// Como partnerStatus, pero distingue la cancelación con menos de 24 h
export function partnerRowStatus(r) {
  if (r.status === 'cancelled' && r.partnerLateCancel === true) {
    return { label: 'Cancelada (menos de 24 h)', color: 'warning' };
  }
  return partnerStatus(r.status);
}

// Precio pactado con el aliado para una categoría (por categoría o default)
export function partnerPriceFor(partner, categoryId) {
  if (!partner) return 0;
  const pricing = partner.pricing || {};
  const byCat = pricing.byCategory || {};
  if (byCat[categoryId] != null) return Number(byCat[categoryId]);
  return Number(pricing.defaultCents || 0);
}

export const partnerCommissionOf = (partner) => Number((partner && partner.commissionCents) ?? DEFAULT_PARTNER_COMMISSION_CENTS);
export const partnerPenaltyOf = (partner) => Number((partner && partner.penaltyCents) ?? DEFAULT_PARTNER_PENALTY_CENTS);

// Penalidad grabada en la inscripción (las anteriores a la V3 no la tienen)
export const partnerPenaltyCentsOf = (r) => Number(r.partnerPenaltyCents ?? DEFAULT_PARTNER_PENALTY_CENTS);

// ¿Se le cobra la penalidad a la armería? Por «No se presentó» o por una
// cancelación con menos de 24 h (partnerLateCancel lo pone el servidor al
// cancelar). El staff la puede quitar o poner a mano desde el detalle de la
// reservación (partnerPenaltyOverride).
export function partnerPenaltyApplies(r) {
  if (r.status !== 'no_show' && r.status !== 'cancelled') return false;
  if (typeof r.partnerPenaltyOverride === 'boolean') return r.partnerPenaltyOverride;
  return r.status === 'no_show' || r.partnerLateCancel === true;
}

// Lo que se le factura a la armería por una inscripción:
//   asistió → precio − comisión · penalidad → $20 sin comisión · resto → $0
export function partnerBillingOf(r) {
  if (r.status === 'completed') {
    const priceCents = Number(r.partnerPriceCents) || 0;
    const commissionCents = Number(r.partnerCommissionCents) || 0;
    return { kind: 'course', priceCents, commissionCents, penaltyCents: 0, billedCents: priceCents - commissionCents };
  }
  if (partnerPenaltyApplies(r)) {
    const penaltyCents = partnerPenaltyCentsOf(r);
    return { kind: r.status === 'no_show' ? 'no_show' : 'late_cancel', priceCents: 0, commissionCents: 0, penaltyCents, billedCents: penaltyCents };
  }
  const upcoming = r.status === 'confirmed' || r.status === 'pending_payment';
  return { kind: upcoming ? 'upcoming' : 'none', priceCents: 0, commissionCents: 0, penaltyCents: 0, billedCents: 0 };
}

// "3 × $5.00" cuando todas las cantidades son iguales; si no, solo "3"
export function countTimes(count, amounts) {
  const uniq = [...new Set(amounts)];
  return uniq.length === 1 ? `${count} × ${money(uniq[0])}` : String(count);
}

// Totales del mes: cursos asistidos (precio − comisión) más las penalidades
// por inasistencia y por cancelar con menos de 24 h.
export function summarizePartnerRows(rows) {
  const bills = rows.map((r) => ({ r, b: partnerBillingOf(r) }));
  const attended = bills.filter((x) => x.b.kind === 'course');
  const noShowPen = bills.filter((x) => x.b.kind === 'no_show');
  const latePen = bills.filter((x) => x.b.kind === 'late_cancel');
  const sum = (list, key) => list.reduce((a, x) => a + x.b[key], 0);

  // Cursos asistidos por curso y tarifa (ej. Renovación 3 × $65, Nueva 2 × $85)
  const lines = new Map();
  attended.forEach(({ r, b }) => {
    const key = `${r.categoryName || 'Curso'}|${b.priceCents}`;
    const line = lines.get(key) || { name: r.categoryName || 'Curso', priceCents: b.priceCents, count: 0, subtotalCents: 0 };
    line.count += 1;
    line.subtotalCents += b.priceCents;
    lines.set(key, line);
  });

  const subtotalCents = sum(attended, 'priceCents');
  const commissionCents = sum(attended, 'commissionCents');
  const noShowPenaltyCents = sum(noShowPen, 'penaltyCents');
  const lateCancelPenaltyCents = sum(latePen, 'penaltyCents');
  return {
    total: rows.length,
    attended: attended.length,
    noShow: rows.filter((r) => r.status === 'no_show').length,
    cancelled: rows.filter((r) => r.status === 'cancelled').length,
    upcoming: rows.filter((r) => r.status === 'confirmed' || r.status === 'pending_payment').length,
    courseLines: [...lines.values()].sort((a, b) => a.name.localeCompare(b.name) || a.priceCents - b.priceCents),
    subtotalCents,
    commissionCents,
    commissionLabel: countTimes(attended.length, attended.map((x) => x.b.commissionCents)),
    noShowPenalties: noShowPen.length,
    noShowPenaltyCents,
    noShowPenaltyLabel: countTimes(noShowPen.length, noShowPen.map((x) => x.b.penaltyCents)),
    lateCancelPenalties: latePen.length,
    lateCancelPenaltyCents,
    lateCancelPenaltyLabel: countTimes(latePen.length, latePen.map((x) => x.b.penaltyCents)),
    penaltyCents: noShowPenaltyCents + lateCancelPenaltyCents,
    billableCount: attended.length + noShowPen.length + latePen.length,
    totalCents: subtotalCents - commissionCents + noShowPenaltyCents + lateCancelPenaltyCents,
  };
}

export const settlementId = (partnerId, monthKey) => `${partnerId}_${monthKey}`;

// ---------------------------------------------------------------------------
// Cobro mensual por ACH (2026-09-28): el cierre es el último día del mes, la
// armería tiene 5 días para pagar (vence el 5) y puede pagar por ACH desde el
// portal o activar el débito automático. La lógica vive en
// functions/partnersAch.js; aquí solo etiquetas y helpers de pantalla.
// ---------------------------------------------------------------------------

// Debe coincidir con TERMS_VERSION en functions/partnersBilling.js
export const TERMS_VERSION = '2026-09-28';
export const TERMS_PDF_PATH = '/terminos-armerias-aliadas.pdf';
export const DEFAULT_PAYMENT_TERM_DAYS = 5;

export const SETTLEMENT_STATUS = {
  open: { label: 'Mes en curso', color: 'default' },
  closed: { label: 'Pendiente de pago', color: 'warning' },
  processing: { label: 'Pago en proceso', color: 'info' },
  paid: { label: 'Pagado', color: 'success' },
  failed: { label: 'Débito rechazado', color: 'error' },
  disputed: { label: 'Disputado', color: 'error' },
  waived: { label: 'Condonado', color: 'default' },
};

// Etiqueta del estado de cuenta; marca "Vencido" si pasó la fecha límite
export function settlementStatus(s, today = todayIso()) {
  if (!s) return SETTLEMENT_STATUS.open;
  const base = SETTLEMENT_STATUS[s.status] || { label: s.status || '—', color: 'default' };
  if ((s.status === 'closed' || s.status === 'failed') && s.dueDate && s.dueDate < today) {
    return { label: s.status === 'failed' ? 'Rechazado · vencido' : 'Vencido', color: 'error', overdue: true };
  }
  return base;
}

export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatDueDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('es-PR', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Fecha límite de pago de un mes (mismo cálculo que el servidor)
export function dueDateFor(monthKey, termDays = DEFAULT_PAYMENT_TERM_DAYS) {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m, Number(termDays) || DEFAULT_PAYMENT_TERM_DAYS);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Último día del mes 'YYYY-MM' como fecha legible
export function monthCloseLabel(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m, 0);
  return d.toLocaleDateString('es-PR', { day: 'numeric', month: 'long' });
}

export const BANK_STATUS = {
  verified: { label: 'Verificada', color: 'success' },
  pending_verification: { label: 'Pendiente de verificar', color: 'warning' },
  errored: { label: 'Error de verificación', color: 'error' },
};
